import { deserialiseAssetManifest, serialiseAssetManifest, type AssetManifest } from "../assets/manifest.ts";
import type { FrameDimensions, TransitionStrip } from "../compile/context.ts";
import type { PropFootprint } from "../compile/footprint.ts";
import type { FrameDepth } from "../compile/ground-depth.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import { asArray, asRecord, asString } from "../document/validate.ts";
import type { RuleSet } from "../terrain/rules.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import type { CataloguePackInfo, CataloguePropInfo, CatalogueTilesetInfo } from "./catalogue.ts";
import { LIBRARY_FRAMES_PER_BATCH } from "./library-held.ts";
import { readCataloguePropEntries, readCatalogueTilesets } from "./late-frames.ts";

/**
 * The frames service's request and answer (ADR-130 decision 2.7): `POST /api/pack-index/frames` takes a body of
 * asset ids and answers the names of a descriptor and its sheets. The pieces here are plain data and pure, so the
 * server that cuts and the tab that stamps read one description.
 */

/**
 * The longest id, in UTF-16 code units.
 */
export const LIBRARY_FRAMES_ID_UNITS = 256;

/**
 * The most bytes of body the route reads: 256 ids of 256 code units at three bytes a unit, with their quotes and
 * commas and the object around them.
 */
export const LIBRARY_FRAMES_BODY_BYTES = 262144;

const SCHEMA_VERSION = 1;

/**
 * A checked request: the ids, sorted by code unit and distinct.
 */
export interface LibraryFramesRequest {
  readonly ids: readonly string[];
}

/**
 * What the route answers in the file `dist/pack-index/frames/<hash>.json`. `assets` is an asset manifest whose
 * pages are the sheets and whose frames are each frame's rect with every field the compile's atlas gives it,
 * extrusion included. The rest are the entries the store takes, as the context holds them when the file is cut:
 * the packs, the catalogue's tilesets and props, the context's footprints, frame sizes, depths and transition
 * strips for those frames, and for a terrain its definition and rule sets. `missing` lists the ids asked for
 * that no pack holds.
 */
export interface LibraryFramesDescriptor {
  readonly schemaVersion: 1;
  readonly assets: AssetManifest;
  readonly packs: readonly CataloguePackInfo[];
  readonly tilesets: readonly CatalogueTilesetInfo[];
  readonly props: readonly CataloguePropInfo[];
  readonly footprints: Readonly<Record<string, PropFootprint>>;
  readonly derivedFootprints: Readonly<Record<string, PropFootprint | null>>;
  readonly frameSizes: Readonly<Record<string, FrameDimensions>>;
  readonly savedDepths: Readonly<Record<string, FrameDepth>>;
  readonly standMarks?: Readonly<Record<string, FrameMarks>>;
  readonly transitionStrips: Readonly<Record<string, TransitionStrip>>;
  readonly terrains: readonly TerrainDefinition[];
  readonly ruleSets: readonly RuleSet[];
  readonly missing: readonly string[];
}

/**
 * Reads and checks a request body: JSON text of an object with the one field `ids`, an array of 1 to 256
 * strings of 1 to 256 code units each.
 *
 * @param text The body's text.
 * @returns The request, or the sentence that refuses it.
 */
export function readLibraryFramesBody(text: string): LibraryFramesRequest | string {
  if (text.length > LIBRARY_FRAMES_BODY_BYTES) {
    return "the body is too long";
  }
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return "the body is not JSON";
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return "the body is an object";
  }
  const keys = Object.keys(value);
  if (keys.length !== 1 || keys[0] !== "ids") {
    return "the body takes ids and no other field";
  }
  const ids = (value as { ids: unknown }).ids;
  if (!Array.isArray(ids) || ids.length < 1 || ids.length > LIBRARY_FRAMES_PER_BATCH) {
    return `ids holds 1 to ${LIBRARY_FRAMES_PER_BATCH} strings`;
  }
  for (const id of ids) {
    if (typeof id !== "string" || id.length < 1 || id.length > LIBRARY_FRAMES_ID_UNITS) {
      return `an id is a string of 1 to ${LIBRARY_FRAMES_ID_UNITS} characters`;
    }
  }
  return { ids: [...new Set(ids as string[])].sort() };
}

/**
 * The text of a descriptor, with its manifest written in the manifest's own order and form.
 *
 * @param descriptor The descriptor.
 * @returns JSON text, one line.
 */
export function serialiseLibraryFrames(descriptor: LibraryFramesDescriptor): string {
  return JSON.stringify({ ...descriptor, assets: JSON.parse(serialiseAssetManifest(descriptor.assets)) as unknown });
}

/**
 * Reads the file the route names.
 *
 * @param text The file's text.
 * @returns The descriptor.
 * @throws Error naming the path when the text is not JSON, holds another version, lacks a part or holds a part of
 *   the wrong shape, or its manifest refuses.
 */
export function readLibraryFrames(text: string): LibraryFramesDescriptor {
  const record = asRecord(JSON.parse(text) as unknown, "library frames");
  if (record["schemaVersion"] !== SCHEMA_VERSION) {
    throw new Error(`library frames schemaVersion must be ${SCHEMA_VERSION}`);
  }
  return {
    schemaVersion: SCHEMA_VERSION,
    assets: deserialiseAssetManifest(JSON.stringify(asRecord(record["assets"], "assets"))),
    packs: asArray(record["packs"], "packs").map((item, i) => {
      const each = asRecord(item, `packs[${i}]`);
      return { id: asString(each["id"], `packs[${i}].id`), name: asString(each["name"], `packs[${i}].name`), ...(each["terrainNames"] === undefined ? {} : { terrainNames: asRecord(each["terrainNames"], `packs[${i}].terrainNames`) as unknown as NonNullable<CataloguePackInfo["terrainNames"]> }) };
    }),
    tilesets: readCatalogueTilesets(record["tilesets"]),
    props: readCataloguePropEntries(record["props"]),
    footprints: asRecord(record["footprints"], "footprints") as Readonly<Record<string, PropFootprint>>,
    derivedFootprints: asRecord(record["derivedFootprints"], "derivedFootprints") as Readonly<Record<string, PropFootprint | null>>,
    frameSizes: asRecord(record["frameSizes"], "frameSizes") as Readonly<Record<string, FrameDimensions>>,
    savedDepths: asRecord(record["savedDepths"], "savedDepths") as Readonly<Record<string, FrameDepth>>,
    ...(record["standMarks"] === undefined ? {} : { standMarks: asRecord(record["standMarks"], "standMarks") as unknown as Readonly<Record<string, FrameMarks>> }),
    transitionStrips: asRecord(record["transitionStrips"], "transitionStrips") as Readonly<Record<string, TransitionStrip>>,
    terrains: asArray(record["terrains"], "terrains") as TerrainDefinition[],
    ruleSets: asArray(record["ruleSets"], "ruleSets") as RuleSet[],
    missing: asArray(record["missing"], "missing").map((id, i) => asString(id, `missing[${i}]`)),
  };
}
